import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BaseModalViewModel } from '../../../../models/base/base-modal-view-model';
import { SmartFiltersDomainModel } from '../../../../domainModels/smart-filters-domain-model';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { SmartFilterCategory } from '../../../../models/automation/smart-filter-category';
import { ConfirmationOptions } from '../../../../models/shared/stylesheet/confirmation-options';
import { BudsenseFile } from '../../../../models/shared/budsense-file';
import { Asset } from '../../../../models/image/dto/asset';
import { debounceTime, filter, map, switchMap, take, tap } from 'rxjs/operators';
import '../../../../utils/subscription.extensions';
import { HydratedSmartFilter } from '../../../../models/automation/hydrated-smart-filter';
import { exists } from '../../../../functions/exists';
import { ModalConfirmation } from '../../../../modals/modal-confirmation';
import { ToastService } from '../../../../modules/toasts/service/toast.service';
import { ProductGroupDomainModel } from '../../../../domainModels/product-group-domain-model';
import { UserDomainModel } from '../../../../domainModels/user-domain-model';
import { AllowedUploadTypesConfig } from '../../../../utils/upload-utils';
import { MediaUtils } from '../../../../utils/media-utils';
import { BsError } from '../../../../models/shared/bs-error';
import {
  AssetAssignmentSession,
  AssetAssignmentSessionConfig
} from '../../../../models/shared/asset-assignment-session';
import {
  AssetAssignmentReconciliationContext,
  AssetAssignmentReconciliationHandler,
  AssignedAssetSnapshot
} from '../../../../models/protocols/asset-assignment-reconciliation';

@Injectable()
export class EditSmartFilterCategoryViewModel
  extends BaseModalViewModel
  implements AssetAssignmentReconciliationHandler
{
  public existingSmartFilterCategory = new BehaviorSubject<SmartFilterCategory>(null);
  public createNewSmartFilterCategory = new SmartFilterCategory();
  public isEditing: boolean = false;
  public imgToUpload: BudsenseFile = null;
  public replaceImg: boolean = false;
  private asset = new BehaviorSubject<Asset>(null);
  public categoryImgAsset$ = this.asset as Observable<Asset>;
  private removeAsset: Asset = null;
  private pendingSavedCategory: SmartFilterCategory | null = null;
  private readonly allowedUploadTypesConfig$: Observable<AllowedUploadTypesConfig> = of({
    allowImage: true,
    allowVideo: false,
    allowPdf: false
  });
  private readonly categoryAssignmentSession = new AssetAssignmentSession(this.buildAssignmentSessionConfig());
  public readonly uploadItemState$ = this.categoryAssignmentSession.uploadItemState$;
  public readonly showUploadStatus$ = this.categoryAssignmentSession.showUploadStatus$;

  public smartFiltersForCategory$: Observable<HydratedSmartFilter[]> = combineLatest([
    this.existingSmartFilterCategory,
    this.smartFiltersDomainModel.curatedSmartFilters$
  ]).pipe(
    filter(sources => sources.every(s => exists(s))),
    debounceTime(100),
    map(([existingCategory, smartFilters]) => smartFilters?.filter(sf => sf.categoryId === existingCategory?.id) ?? [])
  );

  constructor(
    protected activeModal: NgbActiveModal,
    private productGroupDomainModel: ProductGroupDomainModel,
    private smartFiltersDomainModel: SmartFiltersDomainModel,
    private toastService: ToastService,
    private userDomainModel: UserDomainModel,
    router: Router,
    ngbModal: NgbModal,
    private injector: Injector
  ) {
    super(router, ngbModal);
    this.existingSmartFilterCategory.notNull$().subscribeWhileAlive({
      owner: this,
      next: sfc => {
        this.asset.next(sfc?.image);
      }
    });
  }

  save() {
    const saveObject = this.isEditing ? this.existingSmartFilterCategory.getValue() : this.createNewSmartFilterCategory;
    if (exists(this.imgToUpload)) {
      this.categoryAssignmentSession.startFileAssignment(this.imgToUpload, uploadFile =>
        this.saveCategoryWithImage$(saveObject, uploadFile)
      );
      return;
    }
    const lm = 'Saving Parent Group';
    this._loadingOpts.addRequest(lm);
    this.smartFiltersDomainModel.saveSmartFilterCategory$(saveObject, this.imgToUpload, this.removeAsset).subscribe(
      savedCategory => {
        this._loadingOpts.removeRequest(lm);
        this.toastService.publishSuccessMessage('Parent Group Saved', 'Success');
        this.activeModal.close(savedCategory);
      },
      err => {
        this._loadingOpts.removeRequest(lm);
        this.toastService.publishErrorMessage(err.message, 'Failed');
      }
    );
  }

  showDeleteModal() {
    const opts = new ConfirmationOptions();
    opts.title = 'Delete Smart Filter Category';
    opts.bodyText =
      `Are you sure you want to delete this parent group? All curated smart filters that were in this ` +
      `group will be moved to the next available group.\n\nThis action cannot be undone.`;
    opts.cancelText = 'Cancel';
    opts.continueText = 'Continue';
    const confirmation = (confirm: boolean) => {
      if (confirm) {
        this.deleteExisting();
      }
    };
    ModalConfirmation.open$(this.ngbModal, this.injector, opts, confirmation);
  }

  deleteExisting() {
    const lm = 'Deleting Parent Group';
    this._loadingOpts.addRequest(lm);
    this.smartFiltersDomainModel.deleteSmartFilterCategory$(this.existingSmartFilterCategory.getValue()).subscribe({
      next: savedCategory => {
        this._loadingOpts.removeRequest(lm);
        this.toastService.publishSuccessMessage('Parent Group Deleted', 'Success');
        this.activeModal.close(savedCategory);
      },
      error: err => {
        this._loadingOpts.removeRequest(lm);
        this.toastService.publishErrorMessage(err.message, 'Failed');
      }
    });
  }

  // Record the saved category before the S3 step so a retry after a failed image upload updates the new group
  // instead of creating a duplicate.
  private saveCategoryWithImage$(saveObject: SmartFilterCategory, file: BudsenseFile): Observable<string[]> {
    return this.smartFiltersDomainModel.saveSmartFilterCategoryDetails$(saveObject).pipe(
      tap(savedCategory => {
        this.pendingSavedCategory = savedCategory;
        if (!this.isEditing) this.createNewSmartFilterCategory.id = savedCategory?.id;
      }),
      switchMap(savedCategory => this.smartFiltersDomainModel.uploadSmartFilterCategoryImage$(savedCategory, file)),
      map(() => [file.name])
    );
  }

  public clearUploadItemState(): void {
    this.categoryAssignmentSession.clearUploadItemState();
  }

  public handleReplaceFailedUpload(): void {
    this.clearUploadItemState();
    this.imgToUpload = undefined;
    this.replaceImg = true;
  }

  private buildAssignmentSessionConfig(): AssetAssignmentSessionConfig {
    return {
      destroy$: this.onDestroy,
      allowedUploadTypesConfig$: this.allowedUploadTypesConfig$,
      reconciliationHandler: this,
      assignmentSignalTimeoutMs: MediaUtils.ASSET_ASSIGNMENT_TIMEOUT_MS,
      pollRetryCount: MediaUtils.ASSET_RETRY_COUNT,
      pollRetryDelayMs: MediaUtils.ASSET_RETRY_DELAY * 1000,
      onActionFailed: (error: BsError) => this.toastService.publishError(error),
      assignmentTimedOutMessage: 'Parent group image update timed out.',
      uploadFailedMessage: 'Parent group image upload failed.'
    };
  }

  getReconciliationContext$(): Observable<AssetAssignmentReconciliationContext> {
    return this.userDomainModel.userId$.pipe(
      take(1),
      map(userId => ({ userId, locationId: 0, hasTarget: exists(this.pendingSavedCategory?.id) }))
    );
  }

  getAssignedAssetSnapshot$(): Observable<AssignedAssetSnapshot> {
    return this.categoryImgAsset$.pipe(
      take(1),
      map(asset => ({ assetId: asset?.id, timestamp: asset?.timestamp }))
    );
  }

  getAssignedAsset$(): Observable<Asset | null | undefined> {
    return of(this.pendingSavedCategory?.image);
  }

  refreshAssignedAsset$(): Observable<Asset | null | undefined> {
    return this.smartFiltersDomainModel
      .fetchSmartFilterCategory$(this.pendingSavedCategory?.id)
      .pipe(map(category => category?.image));
  }

  buildAssignmentSignal$(): Observable<void> {
    return this.smartFiltersDomainModel.buildReloadSmartFilterCategorySignal$(this.pendingSavedCategory?.id);
  }

  onAssignmentConfirmed(_context: AssetAssignmentReconciliationContext): void {
    this.smartFiltersDomainModel.fetchSmartFilterCategory$(this.pendingSavedCategory?.id).once({
      next: savedCategory => {
        this.smartFiltersDomainModel.refreshSmartFilterCategoriesSubject.next();
        this.clearUploadItemState();
        this.toastService.publishSuccessMessage('Parent Group Saved', 'Success');
        this.activeModal.close(savedCategory ?? this.pendingSavedCategory);
      },
      error: (error: BsError) => this.toastService.publishError(error)
    });
  }

  removeImgClicked() {
    this.replaceImg = true;
    if (this.imgToUpload) {
      this.imgToUpload = undefined;
    } else {
      this.removeAsset = this.asset.getValue();
    }
  }
}
